import type { CSSProperties } from 'react'
import GameCard from './GameCard'
import GameweekDetailHeading from './GameweekDetailHeading'
import type { DetailEntryState } from '../useSharedElementTransition'
import { LIST_STAGGER_ROW_COUNT } from '../sharedElementTransition'
import type { ChipId, GameweekDetail as GameweekDetailType } from '../types'
import './GameweekDetail.css'

interface GameweekDetailProps {
  detail: GameweekDetailType
  onBack: () => void
  onPrevious: () => void
  onNext: () => void
  canGoPrevious: boolean
  canGoNext: boolean
  activeChips: ChipId[]
  /** Set when this is *another player's* gameweek — puts their name in an eyebrow
   *  above the title, so a card full of someone else's picks says whose it is. */
  ownerName?: string
  /** Whether that gameweek is their best one, or just the week a chip was spent. */
  ownerIsBestGameweek?: boolean
  /** Set when the modal was opened by the gameweek picker's shared-element expand:
   *  `state` drives the heading's hidden/revealed handoff, `anchorRef` exposes the heading block so
   *  the flight can measure its destination. Absent when the modal is opened any other way, which
   *  renders it directly with no transition. */
  entryTransition?: {
    state: DetailEntryState
    anchorRef: (el: HTMLElement | null) => void
  }
}

function GameweekDetail({
  detail,
  onBack,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
  activeChips,
  ownerName,
  ownerIsBestGameweek,
  entryTransition,
}: GameweekDetailProps) {
  const isDoubleUpActive = activeChips.includes('doubleUp')
  const isCleanSweepActive = activeChips.includes('cleanSweep')

  // `entering` hides the real heading (the clone is flying in its place) and holds the rest of the
  // modal back; `settled` unfolds it — nav chrome first, then the fixture grid, card by card, the
  // same sequencing the league detail uses. Every other state renders
  // normally.
  const entryState = entryTransition?.state ?? 'idle'
  const entryClass =
    entryState === 'entering'
      ? ' gameweek-detail--entering'
      : entryState === 'settled'
        ? ' gameweek-detail--settled'
        : ''

  return (
    <div className={`gameweek-detail${entryClass}`}>
      <button
        className="gameweek-detail__close gameweek-detail__enter-item"
        style={{ '--enter-order': 1 } as CSSProperties}
        onClick={onBack}
        aria-label="Close"
      >
        ✕
      </button>

      <div className="gameweek-detail__nav">
        <button
          className="gameweek-detail__nav-btn gameweek-detail__enter-item"
          style={{ '--enter-order': 0 } as CSSProperties}
          onClick={onPrevious}
          disabled={!canGoPrevious}
          aria-label="Previous gameweek"
        >
          ‹
        </button>
        <GameweekDetailHeading
          gameweek={detail.gameweek}
          totalPoints={detail.totalPoints}
          activeChips={activeChips}
          ownerName={ownerName}
          ownerIsBestGameweek={ownerIsBestGameweek}
          anchorRef={entryTransition?.anchorRef}
        />
        <button
          className="gameweek-detail__nav-btn gameweek-detail__enter-item"
          style={{ '--enter-order': 0 } as CSSProperties}
          onClick={onNext}
          disabled={!canGoNext}
          aria-label="Next gameweek"
        >
          ›
        </button>
      </div>

      <div className="gameweek-detail__grid">
        {detail.games.map((game, index) => (
          // Each card sits in its own slot so the reveal's opacity/transform stay off GameCard
          // itself, which runs its own transitions (press, dim, reveal). Per-card delay follows
          // the list-row stagger: the first N stagger, the rest share the final delay so they
          // surface together rather than trailing off.
          <div
            key={game.id}
            className="gameweek-detail__card-slot"
            style={
              {
                '--row-index': Math.min(index, LIST_STAGGER_ROW_COUNT - 1),
              } as CSSProperties
            }
          >
            <GameCard
              game={game}
              prediction={detail.predictions[game.id]}
              actualResult={detail.results[game.id]}
              onClick={() => {}}
              isBanker={false}
              isDoubleUpActive={isDoubleUpActive}
              isCleanSweepActive={isCleanSweepActive}
              // Someone else's gameweek: their cards must not say "YOUR PICK".
              isOwnPick={ownerName === undefined}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export default GameweekDetail
